/**
 * BrioDialog -- backdrop-click dismiss and Escape control for
 * native <dialog>, plus the openDialog() Promise wrapper.
 *
 * Native <dialog> handles focus trapping, Escape-to-close, the backdrop,
 * and open/close via commandfor="id" command="show-modal|close".
 * This custom element adds two behaviors on top:
 *
 *   1. Backdrop click closes the dialog when the [dismissible] attribute
 *      is set. Native <dialog> receives clicks outside its content box
 *      (the backdrop pseudo-element itself does not); e.target === the
 *      dialog means the click landed on the backdrop.
 *
 *   2. Escape key is blocked for non-dismissible dialogs, so
 *      confirmation / destructive dialogs require an explicit action.
 *      Blocked at keydown (before cancel fires) because some browsers
 *      ignore preventDefault on cancel for repeated Escape presses;
 *      cancel is kept as a belt-and-suspenders fallback.
 *
 * Expected markup:
 *   <brio-dialog dismissible>
 *       <dialog class="dialog" id="my-dialog">…</dialog>
 *   </brio-dialog>
 *
 * Attributes:
 *   dismissible -- allow backdrop click and Escape to close. Omit for
 *                  confirmation / destructive dialogs that require an
 *                  explicit button press.
 *
 * Programmatic open:
 *   const result = await brio.dialog.open("my-dialog");
 *   if (result === "") return;  // user dismissed
 *
 * Entry/exit animation is fully CSS-driven (@starting-style +
 * transition-behavior: allow-discrete) -- this element does not
 * coordinate animation.
 */

import { BrioBase } from './brio-base.js';

// Pending openDialog() resolvers, keyed by the inner <dialog>. WeakMap
// so that dialogs removed from the DOM without our help still get
// garbage-collected -- the key is held weakly. The wrapper's
// disconnectedCallback below resolves any pending resolver with "" so
// awaiting callers don't hang when the dialog vanishes.
const pendingResolvers = new WeakMap();

class BrioDialog extends BrioBase {
    static tag = 'brio-dialog';
    static {
        customElements.define(BrioDialog.tag, BrioDialog);
    }

    // Private class fields -- enforced at the language level so external
    // code can't reach in and manipulate internal state. Listeners are
    // stored on the instance so disconnectedCallback can remove the exact
    // function references it added.
    #dialog;
    #onKeydown;
    #onCancel;
    #onClick;

    connectedCallback() {
        this.#dialog = this.$('dialog');
        if (!this.#dialog) return;

        this.#onKeydown = (e) => {
            if (e.key === 'Escape' && !this.hasAttribute('dismissible')) {
                e.preventDefault();
            }
        };
        this.#dialog.addEventListener('keydown', this.#onKeydown);

        this.#onCancel = (e) => {
            if (!this.hasAttribute('dismissible')) {
                e.preventDefault();
            }
        };
        this.#dialog.addEventListener('cancel', this.#onCancel);

        this.#onClick = (e) => {
            if (!this.hasAttribute('dismissible')) return;
            if (e.target === this.#dialog && this.#dialog.open) {
                this.#dialog.close();
            }
        };
        this.#dialog.addEventListener('click', this.#onClick);
    }

    disconnectedCallback() {
        if (!this.#dialog) return;
        this.#dialog.removeEventListener('keydown', this.#onKeydown);
        this.#dialog.removeEventListener('cancel', this.#onCancel);
        this.#dialog.removeEventListener('click', this.#onClick);

        // If a caller is awaiting brio.dialog.open() on this dialog and
        // the wrapper is being torn down (route change, removeChild),
        // resolve the pending Promise with "" so the await doesn't hang.
        const resolver = pendingResolvers.get(this.#dialog);
        if (resolver) {
            resolver();
            pendingResolvers.delete(this.#dialog);
        }
    }
}

/**
 * Open a dialog modally and return a Promise that resolves with the
 * dialog's returnValue when the close event fires.
 *
 *   const result = await brio.dialog.open("confirm-delete");
 *   if (result === "delete") proceed();
 *
 * Resolution paths (all return a string):
 *   - Form-submit button inside <form method="dialog"> -- button.value
 *   - <button command="close"> or programmatic dialog.close() -- ""
 *   - Escape on a dismissible dialog -- "" (the native cancel default)
 *   - Backdrop click on a dismissible dialog -- ""
 *   - Wrapper disconnected before close fires -- ""
 *
 * The Promise never rejects under normal use. showModal() itself can
 * throw InvalidStateError synchronously if the dialog is already open;
 * that's a programmer error and bubbles to the caller at the call site.
 *
 * @param {string | HTMLDialogElement} idOrElement
 * @returns {Promise<string>} the dialog's returnValue at close time
 * @throws {Error} when idOrElement doesn't resolve to a dialog
 * @throws {DOMException} from showModal() when the dialog is already open
 */
export function openDialog(idOrElement) {
    const dialog =
        typeof idOrElement === 'string' ? document.getElementById(idOrElement) : idOrElement;

    if (!(dialog instanceof HTMLDialogElement)) {
        throw new Error('brio.dialog.open: expected a dialog id or HTMLDialogElement');
    }

    // showModal() throws synchronously if the dialog is already open --
    // that's caller error and we let it bubble. Calling it before
    // wiring listeners means a throw can't leave a stale entry behind;
    // a successful call can't race with close (synchronous, no
    // microtask boundary between this line and the listener below).
    dialog.showModal();

    return new Promise((resolve) => {
        const onClose = () => {
            pendingResolvers.delete(dialog);
            resolve(dialog.returnValue);
        };
        // { once: true } auto-removes after the first close, so we
        // don't have to track the listener for cleanup on the
        // happy path.
        dialog.addEventListener('close', onClose, { once: true });

        // Disconnect cleanup hook -- the BrioDialog wrapper's
        // disconnectedCallback calls this resolver if the dialog is
        // removed before close fires.
        pendingResolvers.set(dialog, () => {
            dialog.removeEventListener('close', onClose);
            resolve('');
        });
    });
}
